import { useState, useEffect } from "react";
import { Wind, FlaskConical, Leaf, Droplets, Recycle, ShieldAlert } from "lucide-react";
import { TabCompletionHeader } from "./common/TabCompletionHeader";
import { useWizard } from "@/contexts/WizardContext";
import { MetricCard } from "./common/MetricCard";
import { CarbonEmissionsCard } from "./environment/CarbonEmissionsCard";
import { PollutionCard } from "./environment/PollutionCard";
import { BiodiversityCard } from "./environment/BiodiversityCard";
import { WaterCard } from "./environment/WaterCard";
import { WasteCard } from "./environment/WasteCard";
import { RiskManagementCard } from "./environment/RiskManagementCard";
import { WizardSectionNav } from "@/components/wizard/WizardSectionNav";
import { getSectionByCardKey } from "@shared/section-definitions";

export function EnvironmentTab() {
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  
  const {
    environmentOverallCompletion,
    carbonCompletion,
    pollutionCompletion,
    biodiversityCompletion,
    waterCompletion,
    wasteCompletion,
    riskManagementCompletion,
    currentSectionId,
    currentSection,
    goToSection,
    isLocked,
  } = useWizard();

  const environmentStatusLabel = isLocked
    ? "Section finalized"
    : "Section in progress";

  // Sync expandedMetric with currentSection
  useEffect(() => {
    if (currentSection && currentSection.tab === "environment") {
      setExpandedMetric(currentSection.cardKey);
    }
  }, [currentSection]);

  // When a card is clicked, update both local state and context
  const handleSectionExpand = (cardKey: string) => {
    setExpandedMetric(cardKey);
    const section = getSectionByCardKey("environment", cardKey);
    if (section) {
      goToSection(section.id);
    }
  };

  const handleSectionClose = () => {
    setExpandedMetric(null);
  };

  // Section completions for nav - now coming from context
  const sectionCompletions = {
    carbonEmissions: carbonCompletion,
    pollution: pollutionCompletion,
    biodiversity: biodiversityCompletion,
    water: waterCompletion,
    waste: wasteCompletion,
    riskManagement: riskManagementCompletion,
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 hidden lg:block sticky top-[8.25rem] self-start h-[calc(100vh-16.5rem)]">
        <WizardSectionNav
          currentTab="environment"
          currentSectionId={currentSectionId}
          onSectionClick={(section) => handleSectionExpand(section.cardKey)}
          sectionCompletions={sectionCompletions}
        />
      </div>

      {/* Main Content */}
      <div className="min-w-0 flex-1 space-y-4">
        <TabCompletionHeader
          title="Environment"
          completion={environmentOverallCompletion}
          statusLabel={environmentStatusLabel}
        />

        {/* Expanded Carbon Emissions Section */}
        {expandedMetric === "carbonEmissions" && (
          <CarbonEmissionsCard onClose={handleSectionClose} />
        )}

        {/* Expanded Pollution Section */}
        {expandedMetric === "pollution" && (
          <PollutionCard onClose={handleSectionClose} />
        )}

        {/* Expanded Biodiversity Section */}
        {expandedMetric === "biodiversity" && (
          <BiodiversityCard onClose={handleSectionClose} />
        )}

        {/* Expanded Water Section */}
        {expandedMetric === "water" && (
          <WaterCard onClose={handleSectionClose} />
        )}

        {/* Expanded Waste Section */}
        {expandedMetric === "waste" && (
          <WasteCard onClose={handleSectionClose} />
        )}

        {/* Expanded Risk Management Section */}
        {expandedMetric === "riskManagement" && (
          <RiskManagementCard onCollapse={handleSectionClose} />
        )}

        {/* Collapsed Grid View - Show when no metric is expanded */}
        {!expandedMetric && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard
              icon={Wind}
              title="Carbon Emissions"
              description="Track and measure greenhouse gas emissions across Scope 1, 2, and 3"
              completion={carbonCompletion}
              onClick={() => handleSectionExpand("carbonEmissions")}
            />
            <MetricCard
              icon={FlaskConical}
              title="Pollution of Air, Water and Soil"
              description="Monitor pollutant releases to air, water, and soil"
              completion={pollutionCompletion}
              onClick={() => handleSectionExpand("pollution")}
            />
            <MetricCard
              icon={Leaf}
              title="Biodiversity"
              description="Assess impact on ecosystems and species diversity"
              completion={biodiversityCompletion}
              onClick={() => handleSectionExpand("biodiversity")}
            />
            <MetricCard
              icon={Droplets}
              title="Water"
              description="Measure water consumption, quality, and conservation"
              completion={waterCompletion}
              onClick={() => handleSectionExpand("water")}
            />
            <MetricCard
              icon={Recycle}
              title="Resource Use, Circular Economy & Waste"
              description="Track material efficiency, recycling, and waste management"
              completion={wasteCompletion}
              onClick={() => handleSectionExpand("waste")}
            />
            <MetricCard
              icon={ShieldAlert}
              title="Climate risk management"
              description="Climate-related risk identification, assessment, and adaptation"
              completion={riskManagementCompletion}
              onClick={() => handleSectionExpand("riskManagement")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
